/**
 * アカウント管理（運営スタッフ・顧客ユーザーの一覧と操作）。
 *
 * ロール変更と有効/停止をここで行う。サーバ側（API と DB トリガ）が
 * 「最後の有効な最高管理者を失わせる変更」や「運営側⇔顧客側をまたぐ変更」を
 * 拒否するので、この画面の出し分けは操作性のため。**担保はサーバにある。**
 *
 * スタッフ行の操作は最高管理者（staff.manage）のみ。管理者にも一覧は見えるが、
 * ボタンを無効化して理由を示す（黙って 403 にすると壊れて見える）。
 */
import { useEffect, useState } from 'react'
import type { Profile } from '../../lib/supabase.ts'
import {
  listUsers,
  updateUserRole,
  updateUserStatus,
  ApiError,
  type AdminUser,
} from '../../lib/api.ts'
import {
  can,
  isStaffRole,
  roleLabel,
  STAFF_ROLES,
  CUSTOMER_ROLES,
  type Role,
} from '../../lib/roles.ts'

/**
 * エラーコード → 利用者向けの日本語。
 * 未知のコードは生値のまま出す（原因調査で担当者にそのまま伝えてもらうため）。
 */
const ERROR_LABEL: Record<string, string> = {
  last_owner_protected: '最後の最高管理者は降格・停止できません',
  cannot_deactivate_self: '自分自身は停止できません',
  cross_side_change_not_allowed:
    '運営側と顧客側をまたぐ変更はできません（退会→再招待で対応）',
  managed_by_sync: 'CRM同期のユーザーのロールは CRM 側の管理担当者フラグで決まります',
  permission_denied: '権限がありません',
}

const describeError = (e: unknown): string => {
  const code = e instanceof ApiError ? e.code : String(e)
  const label = ERROR_LABEL[code]
  return label ? `${label}（${code}）` : `操作に失敗しました（${code}）`
}

export function AccountSettings({ profile }: { profile: Profile }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // 行内 select の選択値。確定（変更ボタン）まではローカルに持つ
  const [pendingRoles, setPendingRoles] = useState<Record<string, Role>>({})

  const reload = async () => {
    try {
      const { users } = await listUsers()
      setUsers(users)
      setPendingRoles({})
    } catch (e) {
      setError(e instanceof ApiError ? e.code : String(e))
    }
  }

  useEffect(() => {
    reload()
    // eslint 相当の警告は出ないが、初回1回だけでよい（操作後は明示的に reload する）
  }, [])

  if (!users) {
    return error ? (
      <p className="error small">読み込めませんでした（{error}）</p>
    ) : (
      <p className="muted small">読み込み中…</p>
    )
  }

  const changeRole = async (u: AdminUser) => {
    const next = pendingRoles[u.id]
    if (!next || next === u.role) return
    if (!confirm(`${u.email} のロールを「${roleLabel(next)}」に変更します。よろしいですか？`)) return
    setBusy(true)
    setError(null)
    try {
      await updateUserRole(u.id, next)
      await reload()
    } catch (e) {
      setError(describeError(e))
    } finally {
      setBusy(false)
    }
  }

  const toggleStatus = async (u: AdminUser) => {
    const message = u.is_active
      ? `${u.email} を停止します。ログインセッションも失効します。よろしいですか？`
      : `${u.email} を再開します。よろしいですか？`
    if (!confirm(message)) return
    setBusy(true)
    setError(null)
    try {
      await updateUserStatus(u.id, !u.is_active)
      await reload()
    } catch (e) {
      setError(describeError(e))
    } finally {
      setBusy(false)
    }
  }

  const staff = users.filter((u) => isStaffRole(u.role))
  const customers = users.filter((u) => !isStaffRole(u.role))
  // スタッフ行の操作は最高管理者だけ（サーバ側でも 403 になるが UI でも示す）
  const canManageStaff = can(profile.role, 'staff.manage')

  const renderRows = (list: AdminUser[], isStaffSection: boolean) =>
    list.map((u) => {
      const isSelf = u.id === profile.id
      const locked = isStaffSection && !canManageStaff
      // CRM 同期の管理下ではロールは CRM が正（サーバ側でも 409 'managed_by_sync' になる）
      const syncManaged = u.managed_by === 'sync'
      const syncTitle = syncManaged
        ? 'CRM同期のユーザーです。ロールは CRM 側の管理担当者フラグで決まります'
        : undefined
      const options = isStaffSection ? STAFF_ROLES : CUSTOMER_ROLES
      const selected = pendingRoles[u.id] ?? u.role
      return (
        <tr key={u.id}>
          <td className="small">
            {u.display_name ?? '—'}
            {syncManaged && (
              <span className="badge-inline" title="CRM から同期されています（在籍とロールは CRM が正）">
                CRM同期
              </span>
            )}
            <span className="mono small block muted">{u.email}</span>
          </td>
          <td className="small">
            <span className={`role role-${u.role}`}>{roleLabel(u.role)}</span>
          </td>
          {!isStaffSection && <td className="small">{u.org_name ?? '—'}</td>}
          <td className="small">
            {u.is_active ? <span className="success">有効</span> : <span className="error">停止中</span>}
            {/* hold_reason が付いている間、CRM 同期はこのユーザーを勝手に再開しない */}
            {u.hold_reason && (
              <span
                className="warn small block"
                title="スタッフが停止したため、CRM 同期による自動再開はされません（再開はここから行います）"
              >
                停止固定
              </span>
            )}
          </td>
          <td className="small">
            {u.has_login ? 'ログイン可' : <span className="muted">未接続</span>}
          </td>
          <td className="small">
            {isSelf ? (
              <span className="muted">自分（ここでは操作できません）</span>
            ) : (
              <div className="account-actions">
                <select
                  className="inline-role"
                  value={selected}
                  disabled={busy || locked || syncManaged}
                  title={syncTitle}
                  onChange={(e) =>
                    setPendingRoles((prev) => ({ ...prev, [u.id]: e.target.value as Role }))
                  }
                >
                  {options.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
                <button
                  className="small"
                  disabled={busy || locked || syncManaged || selected === u.role}
                  title={syncTitle}
                  onClick={() => changeRole(u)}
                >
                  変更
                </button>
                <button className="small" disabled={busy || locked} onClick={() => toggleStatus(u)}>
                  {u.is_active ? '停止' : '再開'}
                </button>
                {locked && <span className="muted small">最高管理者のみ操作できます</span>}
              </div>
            )}
          </td>
        </tr>
      )
    })

  return (
    <div className="admin">
      <p className="muted small">
        登録済みユーザーのロール変更と有効/停止を行います。停止するとログインセッションも
        失効します。運営側⇔顧客側をまたぐロール変更はできません（退会→再招待で対応）。
        最後の最高管理者を失わせる変更はサーバ側で拒否されます。
      </p>

      {error && <p className="error small">{error}</p>}

      <h3 className="small">運営スタッフ</h3>
      <table className="invites">
        <thead>
          <tr>
            <th>名前 / メール</th>
            <th>ロール</th>
            <th>状態</th>
            <th>ログイン</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>{renderRows(staff, true)}</tbody>
      </table>
      {staff.length === 0 && <p className="muted small">運営スタッフがいません。</p>}

      <h3 className="small">顧客ユーザー</h3>
      <table className="invites">
        <thead>
          <tr>
            <th>名前 / メール</th>
            <th>ロール</th>
            <th>組織</th>
            <th>状態</th>
            <th>ログイン</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>{renderRows(customers, false)}</tbody>
      </table>
      {customers.length === 0 && <p className="muted small">顧客ユーザーがいません。</p>}
    </div>
  )
}
